import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Hint} from '@/components/ui/hint'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {CifraProcedencia} from '@/components/cifra-procedencia'
import {useFijarCifra, type Cifra} from '@/hooks/empresa/organizacion/use-cifras'
import {translateApiError} from '@/lib/errors'

/** Usa los límites que declara el backend (`minimo`/`maximo`/`opciones`). Vacío es inválido. */
function validateCifra(cifra: Cifra, valor: string): string | null {
  if (cifra.tipo === 'opcion') {
    return cifra.opciones.includes(valor) ? null : 'errors.cifraValorInvalido'
  }
  if (!/^\d+$/.test(valor)) return 'errors.cifraValorInvalido'
  const numero = Number(valor)
  return numero >= cifra.minimo && numero <= cifra.maximo ? null : 'errors.cifraValorInvalido'
}

/** Repite la procedencia dentro del modal: guardar con prisa una cifra `propuesto_kairos` la
 * volvería decisión de la empresa sin saberlo. Nunca navega. */
export function CifraModal({
  open,
  onOpenChange,
  businessId,
  cifra,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  cifra: Cifra | null
}) {
  const {t} = useTranslation()
  const fijar = useFijarCifra(businessId)
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const reset = fijar.reset

  useEffect(() => {
    if (open && cifra) {
      setValor(cifra.valor_texto ?? '')
      setMotivo(cifra.motivo ?? '')
      setError(undefined)
      setMotivoError(undefined)
      reset()
    }
  }, [open, cifra, reset])

  if (!cifra) return null
  const abierta = cifra
  // Res. 1843/2025 art. 15: por encima de `justificacion_desde` la periodicidad va
  // «justificada técnicamente».
  const pideMotivo =
    abierta.justificacion_desde != null && /^\d+$/.test(valor.trim()) && Number(valor) > abierta.justificacion_desde

  function blurValor(e: FocusEvent<HTMLInputElement>) {
    setError(validateCifra(abierta, e.target.value.trim()) ?? undefined)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const limpio = valor.trim()
    const hallazgo = validateCifra(abierta, limpio)
    setError(hallazgo ?? undefined)
    const sinMotivo = pideMotivo && motivo.trim() === '' ? 'errors.cifraSinJustificacion' : undefined
    setMotivoError(sinMotivo)
    if (hallazgo || sinMotivo) return
    fijar.mutate(
      {cifraKey: abierta.cifra_key, valorTexto: limpio, motivo: pideMotivo ? motivo.trim() : null},
      {onSuccess: () => onOpenChange(false)}
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`cifras.claves.${abierta.cifra_key}.label`)}</DialogTitle>
          <DialogDescription>{t(`cifras.claves.${abierta.cifra_key}.hint`)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <CifraProcedencia cifra={abierta} />
          {abierta.tipo === 'opcion' ? (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{t('cifras.modal.eligeOpcion')}</legend>
              {abierta.opciones.map((opcion) => (
                <label key={opcion} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="cifra-opcion"
                    value={opcion}
                    data-testid={`cifra-opcion-${opcion}`}
                    checked={valor === opcion}
                    onChange={() => {
                      setValor(opcion)
                      setError(undefined)
                    }}
                    className="mt-0.5 accent-primary"
                  />
                  <span>{t(`cifras.opciones.${abierta.cifra_key}.${opcion}`)}</span>
                </label>
              ))}
              <FieldError error={error} />
            </fieldset>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="cifra-valor">{t(`cifras.unidades.${abierta.cifra_key}`)}</Label>
              <Input
                id="cifra-valor"
                data-testid="cifra-valor-input"
                type="number"
                inputMode="numeric"
                min={abierta.minimo}
                max={abierta.maximo}
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value)
                  setError(undefined)
                }}
                onBlur={blurValor}
                autoFocus
                aria-invalid={!!error}
              />
              <FieldError error={error} />
              <p className="text-xs text-muted-foreground">
                {t('cifras.modal.rango', {minimo: abierta.minimo, maximo: abierta.maximo})}
              </p>
              {/* Res. 1843/2025 art. 15: tope de tres años; el backend lo aplica (`maximo` = 36). */}
              {abierta.cifra_key === 'periodicidad_periodico_meses' && (
                <p className="text-xs text-muted-foreground" data-testid="cifra-advertencia-tope">
                  {t('cifras.modal.advertenciaTope')}
                </p>
              )}
              {pideMotivo && (
                <div className="space-y-2">
                  <Label htmlFor="cifra-motivo">{t('cifras.modal.motivoLabel')}</Label>
                  <Textarea
                    id="cifra-motivo"
                    data-testid="cifra-motivo-input"
                    value={motivo}
                    rows={3}
                    maxLength={1000}
                    onChange={(e) => {
                      setMotivo(e.target.value)
                      setMotivoError(undefined)
                    }}
                    aria-invalid={!!motivoError}
                  />
                  <FieldError error={motivoError} />
                  <Hint testId="cifra-motivo-hint">{t('cifras.modal.motivoHint')}</Hint>
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground" data-testid="cifra-modal-constancia">
            {t('cifras.modal.constancia')}
          </p>
          <FormDialogFooter
            error={fijar.isError ? translateApiError(fijar.error) : null}
            pending={fijar.isPending}
            submitLabel={t('cifras.modal.guardar')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
